import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
} from '@mui/material'
import {
  Visibility,
  VisibilityOff,
  Person,
  Lock,
} from '@mui/icons-material'
import { buildApiUrl, resolveApiBaseUrl } from '../config/api'
import { useAppDispatch } from '../hooks/redux'
import { useLoginMutation } from '../store/api/authApi'
import { apiSlice } from '../store/api/apiSlice'
import { setCredentials, setUser } from '../store/slices/authSlice'
import { gradientBackdrop, gradientBrand } from '../theme/brand'

export default function LoginPage() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [login, { isLoading, error }] = useLoginMutation()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const result = await login({ username, password }).unwrap()
      // 2026-05-27: Clear RTK Query cache TRƯỚC khi set credentials mới —
      // tránh dữ liệu cache từ session/role trước (vd user A đã fetch
      // useGetTeamsQuery, user B login với role student thấy lại cache cũ
      // dù không có quyền). resetApiState xoá cache + abort pending queries.
      dispatch(apiSlice.util.resetApiState())
      // Set token first
      dispatch(setCredentials({ token: result.access_token, user: null }))

      // Fetch user info using RTK Query
      try {
        const apiBaseUrl = await resolveApiBaseUrl()
        const userResponse = await fetch(buildApiUrl(apiBaseUrl, '/auth/me'), {
          headers: {
            'Authorization': `Bearer ${result.access_token}`
          }
        })
        if (userResponse.ok) {
          const userData = await userResponse.json()
          dispatch(setUser(userData))
        }
      } catch (err) {
        console.error('Failed to fetch user info:', err)
      }

      navigate('/dashboard')
    } catch (err) {
      console.error('Login failed:', err)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: gradientBackdrop,
        position: 'fixed',
        inset: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        py: { xs: 2, sm: 4 },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '-50%',
          right: '-20%',
          width: '800px',
          height: '800px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          pointerEvents: 'none',
        },
      }}
    >
      <Container 
        maxWidth="sm" 
        sx={{ 
          position: 'relative', 
          zIndex: 1,
          width: '100%',
          py: 4,
        }}
      >
        <Card
          elevation={24}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}
        >
          {/* Header với gradient */}
          <Box
            sx={{
              background: gradientBrand,
              color: 'white',
              p: 4,
              textAlign: 'center',
            }}
          >
            <Box
              component="img"
              src="/moot-court-navbar-logo.jpg"
              alt="Phiên Toà Giả Định - Công Lý Giới"
              sx={{
                width: 96,
                height: 96,
                objectFit: 'contain',
                mb: 2,
                filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.25))',
              }}
            />
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Gender Justice Moot Court
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Cuộc thi Phiên toà Giả định về Công lý Giới — UEL 2026
            </Typography>
          </Box>

          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" fontWeight="600" align="center" gutterBottom sx={{ mb: 3 }}>
              Đăng nhập
            </Typography>
            
            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                }}
              >
                Đăng nhập thất bại. Vui lòng kiểm tra lại thông tin.
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Tên đăng nhập"
                margin="normal"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
              <TextField
                fullWidth
                label="Mật khẩu"
                type={showPassword ? 'text' : 'password'}
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                disabled={isLoading}
                sx={{
                  mt: 2,
                  mb: 3,
                  py: 1.5,
                  borderRadius: 2,
                  background: gradientBrand,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  boxShadow: '0 4px 18px rgba(20, 78, 140, 0.35)',
                  '&:hover': {
                    background: gradientBrand,
                    filter: 'brightness(1.06)',
                    boxShadow: '0 6px 22px rgba(246, 65, 131, 0.35)',
                  },
                  '&:disabled': {
                    background: 'rgba(0,0,0,0.12)',
                    filter: 'none',
                  },
                }}
              >
                {isLoading ? 'Đang đăng nhập...' : 'Đăng nhập'}
              </Button>
            </Box>
          </CardContent>
        </Card>


        {/* Footer text */}
        <Typography 
          variant="body2" 
          align="center" 
          sx={{ 
            mt: 3, 
            color: 'white',
            opacity: 0.9,
            textShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }}
        >
          © 2026 Gender Justice Moot Court · UEL. Tất cả quyền được bảo lưu.
        </Typography>
      </Container>

      {/* Decorative elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)',
          borderRadius: '50%',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '15%',
          left: '5%',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%)',
          borderRadius: '50%',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
    </Box>
  )
}
